import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import { useBranchEntryScreen } from './hooks/useBranchEntryScreen'
import { appToast } from '../../../components/AppToast'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
      />
    </label>
  )
}

export default function BranchMasterEntryScreen() {
  const { state, actions } = useBranchEntryScreen()

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-8">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Masters</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Branch Master</span>
          </div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-[-0.02em] text-on-surface">Branch Configuration</h1>
          <p className="mt-2 max-w-xl text-base text-on-surface-variant">
            Define organizational hubs and geographic operational centers for real-time resource tracking.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {!state.pendingApproval && (
            <Link
              to="/masters/branch/list"
              className={`inline-flex items-center justify-center rounded-md bg-secondary-container px-7 py-3 text-sm font-bold text-secondary ${
                state.isSaving ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              Discard
            </Link>
          )}

          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading || Boolean(state.pendingApproval)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-8 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">
              {state.pendingApproval ? 'schedule' : 'send'}
            </span>
            {state.pendingApproval ? 'Submitted for Approval' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </section>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-14 text-center text-sm text-on-surface-variant">
          Loading branch details...
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-4">
                <div className="h-10 w-1.5 rounded-full bg-primary" />
                <h2 className="text-3xl font-bold text-on-surface">General Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="md:col-span-2">
                  <TextField
                    label="Branch Name"
                    required
                    value={state.form.name}
                    onChange={(value) => actions.patchForm('name', value)}
                    placeholder="e.g. Northeast Regional Hub"
                  />
                </div>

                <label className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Parent Company <span className="text-error">*</span>
                  </span>
                  <AppDropdown
                    value={state.form.company_id}
                    onChange={(value) => actions.patchForm('company_id', value)}
                    placeholder="Select Company..."
                    triggerClassName="w-full rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface"
                    fetchOptions={actions.fetchCompanyOptions}
                    initialLabel={state.initialLabels.company_name}
                  />
                </label>

                <div className="grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Operational Status
                  </span>
                  <div className="flex items-center rounded-md bg-surface-container-low p-1">
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('active')}
                      className={`flex-1 rounded-md px-3 py-2.5 text-base font-bold transition-all ${
                        state.operationalStatus === 'active'
                          ? 'bg-surface-container-lowest text-primary shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      <span className="material-symbols-outlined mr-1 align-middle text-base">check_circle</span>
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('inactive')}
                      className={`flex-1 rounded-md px-3 py-2.5 text-base font-bold transition-all ${
                        state.operationalStatus === 'inactive'
                          ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                          : 'text-on-surface-variant'
                      }`}
                    >
                      <span className="material-symbols-outlined mr-1 align-middle text-base">pause_circle</span>
                      Inactive
                    </button>
                  </div>
                </div>
              </div>
            </article>

            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-4">
                <div className="h-10 w-1.5 rounded-full bg-secondary" />
                <h2 className="text-3xl font-bold text-on-surface">Location &amp; Contact</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <label className="md:col-span-2 grid gap-2">
                  <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Physical Address</span>
                  <textarea
                    rows={3}
                    value={state.form.address}
                    onChange={(event) => actions.patchForm('address', event.target.value)}
                    placeholder="Enter complete branch address..."
                    className="w-full resize-none rounded-md border-0 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant"
                  />
                </label>

                <TextField
                  label="Email Address"
                  type="email"
                  value={state.form.email}
                  onChange={(value) => actions.patchForm('email', value)}
                  placeholder="branch@bridge.com"
                />

                <TextField
                  label="Phone Number"
                  value={state.form.contact_number}
                  onChange={(value) => actions.patchForm('contact_number', value)}
                  placeholder="+1 (555) 000-0000"
                />

                <TextField
                  label="State"
                  required
                  value={state.form.state}
                  onChange={(value) => actions.patchForm('state', value)}
                  placeholder="State"
                />

                <TextField
                  label="City"
                  value={state.form.city}
                  onChange={(value) => actions.patchForm('city', value)}
                  placeholder="City"
                />

                <TextField
                  label="Country"
                  value={state.form.country}
                  onChange={(value) => actions.patchForm('country', value)}
                  placeholder="Country"
                />

                <TextField
                  label="Pincode"
                  value={state.form.pincode}
                  onChange={(value) => actions.patchForm('pincode', value)}
                  placeholder="Pincode"
                />
              </div>
            </article>
          </div>

          <div className="col-span-12 space-y-6 lg:col-span-4">
            <article className="rounded-[2rem] bg-primary p-6 text-on-primary shadow-xl shadow-primary/10">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] opacity-80">Onboarding Completion</h3>
              <div className="mt-5 flex items-end justify-between">
                <span className="text-5xl font-extrabold">{state.completionPercent}%</span>
                <span className="text-sm opacity-80">{state.completionStepText}</span>
              </div>
              <div className="mt-3 h-3 w-full rounded-full bg-primary-container">
                <div
                  className="h-full rounded-full bg-surface-container-lowest"
                  style={{ width: `${Math.max(8, state.completionPercent)}%` }}
                />
              </div>
              <p className="mt-4 text-sm leading-relaxed opacity-90">
                Please complete the contact details to unlock the Department hierarchy and user assignments for this
                branch.
              </p>
            </article>

            <article className="overflow-hidden rounded-[2rem] bg-surface-container-low">
              <div className="relative h-52">
                <img
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuBdn8r7Y47NIGI3CkgvsVc0HBKacTfaXiUKgkeDPBFOLZhT63ueK0sVrP6H7pUtllVZ7FI9ZuVtVKmj4q9klBMwRsIRt3bvTYiMrz5JbwUVW-dW2J5xY4SCGF4lRSMxVVd8-B8QYp8aXPFwNz32eZLadvrLS90ecJrQCbSfOazc4U2ScQbcTOECq5aTIt_rRTEnLMH2G_sFtgvJqRToduI9_yAvYm1h05oL5CViKHCFPqSKqir-2uetflLDFyL_dVV15CVlJ9n55vo"
                  alt="Branch preview"
                  className="h-full w-full object-cover grayscale"
                />
                <div className="absolute inset-0 bg-primary/20" />
                <div className="absolute bottom-4 left-4 rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white backdrop-blur-sm">
                  Branch Preview
                </div>
              </div>
              <div className="p-6">
                <h4 className="text-xl font-bold text-on-surface">Regional Presence</h4>
                <p className="mt-2 text-sm text-on-surface-variant">
                  Branches act as independent profit centers. Once created, you can configure unique fiscal years and
                  local currency overrides.
                </p>
              </div>
            </article>

            <article className="rounded-[2rem] border-l-4 border-primary bg-surface-container-highest/30 p-6">
              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined mt-1 text-primary">info</span>
                <div>
                  <h4 className="text-xl font-bold text-on-surface">Mandatory Fields</h4>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    System policy requires an active Parent Company to be associated before a branch can be authorized
                    for transactional operations.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  )
}
